import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUserPreferences, updateUserPreferences } from './api';
import { useAuth } from './AuthContext';

export type TableKey = 'product_list' | 'mappings_table' | 'candidates_table';

export type ColumnVisibility = Record<string, boolean>;

export interface ColumnVisibilityMap {
  product_list: ColumnVisibility;
  mappings_table: ColumnVisibility;
  candidates_table: ColumnVisibility;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityMap = {
  product_list: {
    thumbnail: true,
    sku: true,
    title: true,
    brand: true,
    ean: true,
    price: true,
    buybox: true,
    fba_fee: true,
    referral_fee: true,
    profit_amount: true,
    profit_margin: true,
    suggested: true,
    status: true,
  },
  mappings_table: {
    thumbnail: true,
    marketplace: true,
    asin: true,
    amazon_title: true,
    buybox: true,
    fba_fee: true,
    referral_fee: true,
    profit_amount: true,
    profit_margin: true,
    suggested: true,
    confidence: true,
    source: true,
    verified: true,
  },
  candidates_table: {
    marketplace: true,
    asin: true,
    amazon_title: true,
    score_title: true,
    score_brand: true,
    score_ean: true,
    score_attr: true,
    score_composite: true,
    status: true,
  },
};

interface PreferencesContextType {
  columnVisibility: ColumnVisibilityMap;
  isColumnVisible: (table: TableKey, column: string) => boolean;
  toggleColumn: (table: TableKey, column: string) => void;
  setTableColumns: (table: TableKey, columns: ColumnVisibility) => void;
  resetAllColumns: () => void;
  prefsLoaded: boolean;
  saveStatus: SaveStatus;
}

const PreferencesContext = createContext<PreferencesContextType | null>(null);

export const usePreferences = (): PreferencesContextType => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
};

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityMap>(
    () => JSON.parse(JSON.stringify(DEFAULT_COLUMN_VISIBILITY))
  );
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistToBackend = useCallback((vis: ColumnVisibilityMap) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => {
      updateUserPreferences({ column_visibility: vis })
        .then(() => {
          setSaveStatus('saved');
          if (savedFadeRef.current) clearTimeout(savedFadeRef.current);
          savedFadeRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
        })
        .catch(() => {
          setSaveStatus('error');
          if (savedFadeRef.current) clearTimeout(savedFadeRef.current);
          savedFadeRef.current = setTimeout(() => setSaveStatus('idle'), 4000);
        });
    }, 400);
  }, []);

  useEffect(() => {
    if (!user) {
      setColumnVisibility(JSON.parse(JSON.stringify(DEFAULT_COLUMN_VISIBILITY)));
      setPrefsLoaded(false);
      setSaveStatus('idle');
      return;
    }

    let cancelled = false;
    getUserPreferences()
      .then((res) => {
        if (cancelled) return;
        const cv = res.data?.preferences?.column_visibility;
        if (cv) {
          setColumnVisibility((prev) => {
            const merged = { ...prev };
            for (const table of Object.keys(merged) as TableKey[]) {
              if (cv[table]) {
                merged[table] = { ...merged[table], ...cv[table] };
              }
            }
            return merged;
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPrefsLoaded(true);
      });

    return () => { cancelled = true; };
  }, [user]);

  const isColumnVisible = useCallback(
    (table: TableKey, column: string): boolean => {
      return columnVisibility[table]?.[column] ?? true;
    },
    [columnVisibility]
  );

  const toggleColumn = useCallback(
    (table: TableKey, column: string) => {
      setColumnVisibility((prev) => {
        const tableVis = { ...prev[table] };
        const visibleCount = Object.values(tableVis).filter(Boolean).length;
        if (tableVis[column] && visibleCount <= 2) return prev;
        tableVis[column] = !tableVis[column];
        const next = { ...prev, [table]: tableVis };
        persistToBackend(next);
        return next;
      });
    },
    [persistToBackend]
  );

  const setTableColumns = useCallback(
    (table: TableKey, columns: ColumnVisibility) => {
      setColumnVisibility((prev) => {
        const next = { ...prev, [table]: columns };
        persistToBackend(next);
        return next;
      });
    },
    [persistToBackend]
  );

  const resetAllColumns = useCallback(() => {
    const defaults = JSON.parse(JSON.stringify(DEFAULT_COLUMN_VISIBILITY));
    setColumnVisibility(defaults);
    persistToBackend(defaults);
  }, [persistToBackend]);

  return (
    <PreferencesContext.Provider
      value={{ columnVisibility, isColumnVisible, toggleColumn, setTableColumns, resetAllColumns, prefsLoaded, saveStatus }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
